<template>
    <div>
        <card title="PBM Record">
            <b-row id="search-row">
                <b-col>
                    <SearchComponent :id="PBMRecord && +PBMRecord.id" v-model="PBMRecord" label="PBM"
                        search-u-r-l="/PharmacyBenefitManager" create-new-object create-new-label="Create PBM"
                        prepopulate :disabled="!disableFields" @input="onPBMChange" />
                </b-col>
            </b-row>
            <b-row id="details-row">
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.name" label="PBM" max-length="30" required :disabled="disableFields" />
                </b-col>
                <b-col cols="6">
                    <EnumSelector v-model="PBMFlag" :disabled="disableFields" :enum-codes="PBMflags" label="Flag"
                        required
                        help-html="<p>Special-handling flags for this PBM. <strong>CovidPcn</strong> was added for COVID-era PCN routing; leave as <em>None</em> unless you have a specific reason to enable it.</p>" />
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.vendorId" label="VendorId" max-length="50" :disabled="disableFields">
                        <template #label>
                            VendorId
                            <HelpPopup>
                                <p>Our vendor/software ID sent in the NCPDP claim header when a plan bound to this PBM submits a claim. Most PBMs don't require it — leave blank unless the PBM has assigned you a specific value.</p>
                                <p>
                                    <strong>Caremark:</strong> use
                                    <code class="copy-code" role="button" tabindex="0"
                                        title="Click to copy"
                                        @click.stop="copyToClipboard('D012500010')"
                                        @keydown.enter.stop.prevent="copyToClipboard('D012500010')"
                                        @keydown.space.stop.prevent="copyToClipboard('D012500010')">D012500010</code>
                                </p>
                            </HelpPopup>
                        </template>
                    </fg-input>
                </b-col>
                <b-col cols="6">
                    <fg-input label="Minimum DS Mail Order NPI" type="number"
                        v-model.number="PBMRecord.mailOrderDaySupply" :disabled="disableFields">
                        <template #label>
                            Minimum DS Mail Order NPI
                            <HelpPopup>
                                <p>Day-supply threshold at which claims switch from the store's retail NPI to its mail-order NPI.</p>
                                <p>When a prescription's day-supply is <strong>≥</strong> this value, the plan is flagged to use the mail-order NPI, and the store has one configured, the mail-order NPI is used on the claim. Leave blank to always use the retail NPI - the claim is then still subject to this PBM's allowed states below.</p>
                                <p>Zero is <strong>not</strong> the same as blank: every fill with a recorded day supply meets it, so flagged plans always bill mail-order, and any store with a mail-order NPI skips the allowed-states check below.</p>
                            </HelpPopup>
                        </template>
                    </fg-input>
                </b-col>
            </b-row>
            <b-row id="price-schedule-row">
                <b-col cols="6">
                    <SearchComponent :id="PBMRecord.defaultPriceSchedule && +PBMRecord.defaultPriceSchedule.id"
                        v-model="PBMRecord.defaultPriceSchedule"
                        label="Default Price Schedule"
                        search-u-r-l="/PriceSchedule"
                        :disabled="disableFields"
                        prepopulate clearable
                        help-html="<p>Fallback pricing basis, factor, and markup applied to claims under this PBM when no plan-specific price schedule is set. Leave blank to fall through to whichever schedule the plan itself defines.</p>"
                        @input="onPriceScheduleChange" />
                </b-col>
                <b-col cols="6">
                    <div v-if="PBMRecord.defaultPriceSchedule" class="form-group">
                        <label>Price Schedule Details:</label>
                        <div class="text-muted small">
                            <div>Basis: {{ getBasisName(PBMRecord.defaultPriceSchedule.basis) }}</div>
                            <div>Factor: {{ PBMRecord.defaultPriceSchedule.factor }}</div>
                            <div v-if="PBMRecord.defaultPriceSchedule.flatMarkup">Flat Markup: ${{ PBMRecord.defaultPriceSchedule.markup }}</div>
                            <div v-else>Markup: {{ (PBMRecord.defaultPriceSchedule.markup * 100).toFixed(2) }}%</div>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <b-row id="compound-billing-row">
                <b-col cols="12">
                    <h5 class="mt-3 mb-2">
                        Compound Billing Configuration
                        <HelpPopup>
                            <p>NCPDP fields used when transmitting a compound prescription's claim.</p>
                            <p>These override the defaults for this PBM only. Leave blank unless the PBM has published specific compound-billing requirements.</p>
                        </HelpPopup>
                    </h5>
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.compoundProductServiceQualifier"
                              label='Compound Product Service Qualifier'
                              max-length="2"
                              :disabled="disableFields">
                        <template #label>
                            Compound Product Service Qualifier
                            <small class="text-muted">(NCPDP E1 field, defaults to "00" if not specified)</small>
                        </template>
                    </fg-input>
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.compoundProductServiceId"
                              label='Compound Product Service ID'
                              max-length="19"
                              :disabled="disableFields">
                        <template #label>
                            Compound Product Service ID
                            <small class="text-muted">(NCPDP D7 field, defaults to "0" if not specified)</small>
                        </template>
                    </fg-input>
                </b-col>
                <b-col cols="6">
                    <Checkbox v-model="PBMRecord.sendCompoundType"
                              :disabled="disableFields">
                        Send Compound Type
                        <small class="text-muted">(NCPDP G1 field, defaults to not sent)</small>
                    </Checkbox>
                </b-col>
            </b-row>
            <b-row id="allowed-states-row">
                <b-col cols="12">
                    <div class="font-weight-bold d-block mt-3 mb-1">
                        Store / Allowed States
                        <HelpPopup>
                            <p>Restricts which of your stores can bill this PBM for prescriptions shipped to which states.</p>
                            <p>When a claim is submitted, if the store isn't allowed to ship to the patient's state under this PBM the claim is blocked — unless the drug's NDC11 is listed under <strong>Exempt Drugs</strong> below.</p>
                            <p>Pick a store, then choose the states it's permitted to ship to. Click a chip to re-open it for editing; the <em>×</em> removes the store's mapping entirely.</p>
                        </HelpPopup>
                    </div>
                    <div class="store-editor-row">
                        <div class="store-editor-picker" :class="{ disabled: disableFields }">
                            <search-component :id="allowedStore && +allowedStore.id"
                                v-model="allowedStore" label="Store" search-u-r-l="/Store" prepopulate
                                :disabled="disableFields" />
                        </div>
                        <div class="store-editor-states">
                            <label class="store-editor-states-spacer" aria-hidden="true">&nbsp;</label>
                            <multiselect v-model="allowedStatesForStore" :options="stateOptions"
                                group-label="label" group-values="states" multiple group-select
                                :close-on-select="false" :clear-on-select="false"
                                :placeholder="allowedStore.name?.length > 0
                                    ? 'Allowed states for ' + allowedStore.name
                                    : 'Select a store to edit its states'"
                                :disabled="disableFields || !allowedStore.id">
                                <template #selection="{ values, search, isOpen }">
                                    <span class="multiselect__single" v-if="values.length" v-show="!isOpen">
                                        {{ values.length }} states selected
                                    </span>
                                </template>
                            </multiselect>
                        </div>
                    </div>
                    <div v-if="configuredStoreEntries.length" class="store-chip-row mt-2">
                        <button v-for="entry in configuredStoreEntries" :key="entry.storeId" type="button"
                            class="store-chip"
                            :class="{ 'store-chip-active': allowedStore.id != null && entry.storeId == allowedStore.id }"
                            :disabled="disableFields"
                            :title="entry.isAllStates ? 'All states' : entry.states.join(', ')"
                            @click="editStoreEntry(entry)">
                            <span class="store-chip-name">{{ entry.storeName }}</span>
                            <span class="store-chip-summary">{{ entry.summary }}</span>
                            <span class="store-chip-remove" aria-label="Remove store"
                                @click.stop="removeStoreEntry(entry)">
                                <i class="fa fa-times"></i>
                            </span>
                        </button>
                    </div>
                </b-col>
            </b-row>
            <b-row id="lists-row">
                <b-col id="bins-col">
                    <b-row>
                        <b-col>
                            <fg-input @keyup.enter="binEntered" max-length="6" label="BINs" :disabled="disableFields">
                                <template #label>
                                    BINs
                                    <HelpPopup>
                                        <p>NCPDP Bank Identification Numbers that map to this PBM. When a patient submits an insurance card whose BIN matches one of these, claims are routed to this PBM.</p>
                                        <p>Type a 6-digit BIN and press <kbd>Enter</kbd> to add. Click <em>×</em> next to a BIN to remove it.</p>
                                    </HelpPopup>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col v-for="bin in sortedBins" :key="bin" cols="2">
                            {{ bin }}
                            <b-button-close @click="removeBin(bin)" text-variant="danger" />
                        </b-col>
                    </b-row>
                </b-col>
                <b-col id="exempt-ndc-col">
                    <b-row>
                        <b-col>
                            <fg-input @keyup.enter="ndcEntered" max-length="13" label="Exempt Drugs (NDC11)"
                                :disabled="disableFields">
                                <template #label>
                                    Exempt Drugs (NDC11)
                                    <HelpPopup>
                                        <p>NDC11s that bypass the <strong>Store / Allowed States</strong> restriction. Claims for these drugs can be billed to this PBM even when the store isn't allowed to ship to the patient's state.</p>
                                        <p>Enter an 11-digit NDC (hyphens accepted, will be stripped) and press <kbd>Enter</kbd> to add.</p>
                                    </HelpPopup>
                                </template>
                            </fg-input>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col v-for="ndc in PBMRecord.exemptedNdcs" :key="ndc" cols="3">
                            {{ ndc }}
                            <b-button-close @click="removeNdc(ndc)" text-variant="danger" />
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <NewClearSaveButtons ref="newClearSaveButtons"
                :titles="_buttonTitles"
                :disable="disableFields" :disable-clear="disableFields" :disable-save="!isFormValid"
                disable-save-after-click
                @cancel-clicked="backToPatientDashboard" @clear-clicked="showConfirmationModal" @new-clicked="createPBM"
                @save-clicked="submitPBM" />
        </card>
    </div>
</template>
<script lang="ts">
import { Flags, PharmacyBenefitManager } from '@/models/PharmacyBenefitManager';
import { PriceSchedule, BasisEnum } from '@/models/PriceSchedule';
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import NewClearSaveButtons, { ButtonTitles } from '@/components/NewClearSaveButtons.vue';
import axios from 'axios';
import EnumSelector from '@/components/EnumSelector.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import SearchComponent from "@/components/SearchComponent.vue";
import Multiselect from "vue-multiselect";
import { ValidValues } from '@/data/ValidValues';
import NotificationTypes = NotificationOptions.NotificationTypes;
import { Store } from "@/models/Store";
import { ButtonTitles } from "@/components/ButtonTitles";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import { copyToClipboard as copyTextToClipboard } from "@/util/Clipboard";

@Component({
    name: "PBMComponent",
    components: {
        Multiselect,
        SearchComponent,
        NewClearSaveButtons,
        EnumSelector,
        Checkbox,
        HelpPopup,
    },
})
export default class PBMComponent extends Vue {
    private allowedStore: Store = new Store();
    private PBMRecord: PharmacyBenefitManager = new PharmacyBenefitManager();
    @Ref() newClearSaveButtons!: NewClearSaveButtons;

    protected PBMflags = Flags;
    private PBMFlag: number = 0;
    @Prop({ type: Boolean, default: false }) private createNewPbm!: boolean;
    @Prop({ type: Boolean, default: false }) private noCancelButton!: boolean;
    @Prop({ type: Number, default: null }) private pbmId!: number | null;
    private stateOptions = [{ label: "All States", states: ValidValues.addressStates }];
    private storesById: Record<number, Store> = {};
    // Fallback "Store #<id>" placeholders built by editStoreEntry; tracked so we never persist
    // one as a real name (which would stop loadStoreNames from ever fetching the true name).
    private stubStores = new WeakSet<Store>();

    get allowedStatesForStore(): string[] {
        const idx = this.PBMRecord.allowedStates.findIndex(value => value.storeId == this.allowedStore.id);
        if (idx === -1) return [];
        return this.PBMRecord.allowedStates[idx].states;
    }

    // noinspection JSUnusedGlobalSymbols
    set allowedStatesForStore(value: string[]) {
        const idx = this.PBMRecord.allowedStates.findIndex(value => value.storeId == this.allowedStore.id);
        if (idx === -1) {
            this.PBMRecord.allowedStates.push({ storeId: +this.allowedStore.id, states: value });
        } else {
            this.PBMRecord.allowedStates[idx].states = value;
        }
        this.cacheStore(this.allowedStore);
    }

    get configuredStoreEntries() {
        const validStates = ValidValues.addressStates;
        return this.PBMRecord.allowedStates.map(entry => {
            const store = this.storesById[entry.storeId];
            // "All states" = the entry covers every valid address state. Test set membership
            // rather than a raw length match so stale or duplicated codes can't make a partial
            // list read as "All states" (or a full-but-duplicated list read as partial).
            const selected = new Set(entry.states);
            const isAllStates = validStates.length > 0 && validStates.every(s => selected.has(s));
            let summary: string;
            if (isAllStates) summary = 'All states';
            else if (entry.states.length === 0) summary = 'No states';
            else if (entry.states.length <= 3) summary = entry.states.join(', ');
            else summary = `${entry.states.length} states`;
            return {
                storeId: entry.storeId,
                storeName: store?.name || `Store #${entry.storeId}`,
                states: entry.states,
                isAllStates,
                summary,
            };
        });
    }

    private cacheStore(store: Store) {
        if (!store?.id) return;
        // Never cache a placeholder stub; leaving the id uncached lets loadStoreNames keep
        // trying to resolve the real name.
        if (this.stubStores.has(store)) return;
        if (!this.storesById[store.id]) {
            this.storesById = { ...this.storesById, [store.id]: store };
        }
    }

    private async loadStoreNames() {
        const ids = this.PBMRecord.allowedStates
            .map(a => a.storeId)
            .filter(id => id && !this.storesById[id]);
        if (ids.length === 0) return;
        const responses = await Promise.all(
            ids.map(id => axios.get<Store>(`/Store/${id}`).catch(() => null)),
        );
        const next: Record<number, Store> = { ...this.storesById };
        responses.forEach((res, i) => {
            if (res?.data) next[ids[i]] = res.data;
        });
        this.storesById = next;
    }

    editStoreEntry(entry: { storeId: number; storeName: string }) {
        const cached = this.storesById[entry.storeId];
        if (cached) {
            this.allowedStore = cached;
            return;
        }
        const stub = new Store();
        stub.id = entry.storeId;
        stub.name = entry.storeName;
        this.stubStores.add(stub);
        this.allowedStore = stub;
    }

    removeStoreEntry(entry: { storeId: number }) {
        const idx = this.PBMRecord.allowedStates.findIndex(a => a.storeId === entry.storeId);
        if (idx !== -1) this.PBMRecord.allowedStates.splice(idx, 1);
        if (this.allowedStore.id == entry.storeId) this.allowedStore = new Store();
    }

    @Watch('allowedStore')
    onAllowedStoreChanged(store: Store) {
        this.cacheStore(store);
    }

    created() {
        if (this.createNewPbm) {
            this.createPBM();
        } else if (this.pbmId) {
            // Load the PBM if an ID was provided
            this.loadPBM(this.pbmId);
        }
    }

    @Watch('pbmId')
    onPBMIdChanged(newVal: number | null, oldVal: number | null) {
        if (newVal && newVal != oldVal) {
            this.loadPBM(newVal);
        }
    }

    async loadPBM(id: number) {
        try {
            const response = await axios.get(`/PharmacyBenefitManager/${id}`);
            // Reset per-PBM editing state only once the new record is in hand. This component is
            // reused across navigations, so resetting before a failed GET would blank the store
            // names of the record still displayed on screen.
            this.allowedStore = new Store();
            this.storesById = {};
            this.PBMRecord = response.data;
            this.PBMFlag = this.PBMRecord.flags;
            await this.loadStoreNames();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Error loading PBM:", error);
        }
    }

    clearForm() {
        this.PBMRecord = new PharmacyBenefitManager();
        this.allowedStore = new Store();
        this.storesById = {};
        this.PBMFlag = 0;
        // Match createPBM: the embedded audit form clears in place; only the standalone page
        // resets the URL to the base PBM route.
        if (!this.createNewPbm && this.$route.name !== 'PharmacyBenefitManager') {
            this.$router.push({ name: 'PharmacyBenefitManager' });
        }
    }

    createPBM() {
        this.PBMRecord = new PharmacyBenefitManager();
        this.PBMRecord.id = 0;
        this.allowedStore = new Store();
        this.storesById = {};
        this.PBMFlag = 0;
        // When embedded inline in AuditPage (create-new-pbm), stay put and communicate via events;
        // only the standalone page should reset the URL to the base PBM route.
        if (!this.createNewPbm && this.$route.name !== 'PharmacyBenefitManager') {
            this.$router.push({ name: 'PharmacyBenefitManager' });
        }
    }

    backToPatientDashboard() {
        if (this.createNewPbm) {
            this.$emit("close");
            return;
        }
    }

    showConfirmationModal() {
        this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
            title: 'Confirm',
            okVariant: 'danger',
            centered: true,
        })
            .then(val => {
                if (val) this.clearForm();
            })
            .catch(err => {
                console.log("Error caught in showConfirmationModal()");
                console.error(err);
            });
    }

    submitPBM() {
        const name = this.PBMRecord?.name;
        if (name == null || name === "") {
            this.$notification(NotificationOptions.warning("PBM name is required."));
            (this.newClearSaveButtons as any).resetSaveState();
            return;
        }
        this.PBMRecord.flags = this.PBMFlag;
        // v-model.number leaves "" on a cleared input, which int? can't bind server-side.
        if (!Number.isFinite(this.PBMRecord.mailOrderDaySupply)) this.PBMRecord.mailOrderDaySupply = null;
        // createPBM sets id = 0 for a brand-new record; an edited record carries its real id.
        const isNew = !this.PBMRecord.id;
        axios.post<PharmacyBenefitManager>(`/PharmacyBenefitManager`, this.PBMRecord)
            .then(res => {
                this.$notification(NotificationOptions.notificationOptionsPreset(
                    isNew ? "Pharmacy Benefit Manager Created Successfully" : "Pharmacy Benefit Manager Updated Successfully",
                    NotificationOptions.NotificationTypes.success));
                this.PBMRecord = res.data;
                const savedId = res.data?.id;
                // Embedded (create-new-pbm) reports back via the `created` event below;
                // only the standalone page should route to the new PBM's Details URL.
                if (!this.createNewPbm && savedId && this.$route.params.id !== String(savedId)) {
                    this.$router.push({ name: 'PharmacyBenefitManagerDetails', params: { id: String(savedId) } });
                }
                (this.newClearSaveButtons as any).resetSaveState();
                this.$emit('created', this.PBMRecord);
            })
            .catch(err => {
                (this.newClearSaveButtons as any).resetSaveState();
                this.$notification(NotificationOptions.error(err));
                console.error("Error while saving PBM.", { err, response: err.response });
            });
    }

    ndcEntered(val: any) {
        let tarVal = val.target.value;
        let idx = this.PBMRecord.exemptedNdcs.indexOf(tarVal);
        if (idx != -1) {
            this.$notification(NotificationOptions.notificationOptionsPreset("NDC already present", NotificationTypes.danger));
            return;
        }
        if (tarVal.length == 13) {
            tarVal = tarVal.replace('-', '');
        }
        if (tarVal.length == 11) {
            this.PBMRecord.exemptedNdcs.push(tarVal);
        } else {
            this.$notification(NotificationOptions.notificationOptionsPreset("Invalid NDC11 length", NotificationTypes.danger));
        }
    }

    binEntered(val: any) {
        const tarVal = val.target.value;
        let idx = this.PBMRecord.bins.indexOf(tarVal);

        if (idx != -1) {
            this.$notification(NotificationOptions.notificationOptionsPreset("BIN already present", NotificationTypes.danger));
            return;
        }

        if (tarVal.length == 6) {
            this.PBMRecord.bins.push(tarVal);
        } else {
            this.$notification(NotificationOptions.warning("Invalid BIN length"));
        }
    }

    removeNdc(val: any) {
        let idx = this.PBMRecord.exemptedNdcs.indexOf(val);
        if (idx != -1) {
            this.PBMRecord.exemptedNdcs.splice(idx, 1);
        }
    }

    removeBin(val: any) {
        let idx = this.PBMRecord.bins.indexOf(val);
        if (idx != -1) {
            this.PBMRecord.bins.splice(idx, 1);
        }
    }

    async onPBMChange(value: PharmacyBenefitManager) {
        if (!value?.id) {
            this.PBMRecord = new PharmacyBenefitManager();
            if (this.$route.name !== 'PharmacyBenefitManager') {
                this.$router.push({ name: 'PharmacyBenefitManager' });
            }
            return;
        }
        const targetId = String(value.id);
        if (this.$route.params.id !== targetId) {
            try {
                // pbmId watcher will pick up the new route param and call loadPBM.
                await this.$router.push({ name: 'PharmacyBenefitManagerDetails', params: { id: targetId } });
            } catch (navigationError) {
                // Duplicated navigations and guard redirects (e.g. beforeEach -> /unauthorized) are
                // expected outcomes. Anything else means the route never changed and the PBM won't
                // load, so surface it rather than failing silently.
                const benign = isNavigationFailure(navigationError, NavigationFailureType.duplicated)
                    || isNavigationFailure(navigationError, NavigationFailureType.redirected);
                if (!benign) {
                    this.$notification(NotificationOptions.error(navigationError));
                    console.error("Error navigating to PBM details:", navigationError);
                }
            }
        } else {
            await this.loadPBM(value.id);
        }
    }

    get sortedBins(): string[] {
        return this.PBMRecord?.bins?.sort();
    }

    get disableFields(): boolean {
        return isNaN(this.PBMRecord.id);
    }

    get isFormValid() {
        return true;
    }

    onPriceScheduleChange(value: PriceSchedule) {
        this.PBMRecord.defaultPriceSchedule = value;
        this.PBMRecord.defaultPriceScheduleId = value?.id || null;
    }

    async copyToClipboard(value: string) {
        if (await copyTextToClipboard(value)) {
            this.$notification(NotificationOptions.notificationOptionsPreset(
                `Copied "${value}" to clipboard`,
                NotificationTypes.success,
            ));
        } else {
            this.$notification(NotificationOptions.error("Could not copy to clipboard."));
        }
    }

    getBasisName(basis: BasisEnum): string {
        switch (basis) {
            case BasisEnum.AWP:
                return 'AWP (Average Wholesale Price)';
            case BasisEnum.Cost:
                return 'Cost';
            default:
                return 'Unknown';
        }
    }

    private get _buttonTitles() {
        const res: ButtonTitles = {
            save: "Save Changes",
            clear: "Clear",
            new: "Create New PBM",
            cancel: this.noCancelButton ? "" : "Back to Audit Record",
        };
        return res;
    }
}
</script>

<style scoped>
.store-chip-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
}

.store-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px 4px 10px;
    border: 1px solid #d6d8db;
    border-radius: 16px;
    background: #fff;
    font-size: 0.85rem;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.store-chip:hover:not(:disabled) {
    border-color: #9ea3a8;
}

.store-chip:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.store-chip-active {
    border-color: #1779ba;
    background: #eaf3fb;
    box-shadow: 0 0 0 2px rgba(23, 121, 186, 0.15);
}

.store-chip-name {
    font-weight: 600;
    color: #212529;
}

.store-chip-summary {
    color: #495057;
    font-size: 0.78rem;
}

.store-chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    color: #6c757d;
    font-size: 0.7rem;
}

.store-chip-remove:hover {
    background: rgba(0, 0, 0, 0.08);
    color: #c92a2a;
}

.store-editor-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 12px;
}

.store-editor-row :deep(.v-select),
.store-editor-row :deep(.multiselect) {
    /* Match the picker (v-select) and states multiselect so their input rows line up. */
    min-height: 38px;
}

.store-editor-row :deep(.style-chooser .vs__dropdown-toggle) {
    min-height: 38px;
}

.store-editor-picker {
    min-width: 220px;
    flex: 0 1 280px;
}

.store-editor-picker :deep(.form-group) {
    margin-bottom: 0;
}

.store-editor-picker.disabled {
    opacity: 0.6;
    pointer-events: none;
}

.store-editor-states {
    min-width: 240px;
    flex: 0 1 320px;
}

.store-editor-states :deep(.multiselect) {
    min-height: 38px;
    font-size: 0.85rem;
}

.copy-code {
    cursor: pointer;
    padding: 1px 4px;
    border-radius: 3px;
    background: rgba(23, 121, 186, 0.08);
    color: #1779ba;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
}

.copy-code:hover,
.copy-code:focus {
    background: rgba(23, 121, 186, 0.16);
    outline: none;
}
</style>
